import React, { useState } from 'react';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  InputText,
  InputTextErrorMode,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import Select, { ValueType } from 'react-select';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { PaymentUIModel } from '../../../../../shared/ui-models/payment.ui-model';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import ReactDatePicker from 'react-datepicker';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import { PaymentCollector } from '../../../../../shared/enums/payment-collector.enum';
import { PaymentType } from '../../../../../shared/enums/payment-type.enum';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { FeeType } from '../../../../../shared/enums/amount-type.enum';
import { PaymentItemType } from '../../../../../shared/enums/payment-item-type.enum';
import { PaymentItemModel } from '../../../../../shared/models/payment-item.model';
import { hasAccess } from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';

export interface AddPaymentModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    reservation: ReservationDetailsUiModel | undefined;
  };
  eventHandlers: {
    closeHandler: () => void;
    addPaymentHandler: (paymentData: PaymentUIModel) => void;
  };
}

/**
 * functional component AddPaymentModal
 * @param {AddPaymentModalProps}
 */
export const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  config,
  eventHandlers,
  data
}) => {
  const [newPaymentItems, setNewPaymentItems] = useState<PaymentItemModel[]>([
    {
      feeName: '',
      feeType: FeeType.AMOUNT,
      itemType: undefined,
      feeValue: 0
    }
  ]);

  const [newPaymentDueDate, setNewPaymentDueDate] = useState<FormElementData<Date>>({
    value: new Date(),
    isValid: true
  });
  const [newPaymentDirection, setNewPaymentDirection] = useState<FormElementData<'in' | 'out'>>({
    value: 'in',
    isValid: true
  });

  const [newPaymentCurrency, setNewPaymentCurrency] = useState<FormElementData<'egp' | 'usd'>>({
    value: 'egp',
    isValid: true
  });

  const isMobileView = useWindowResizer();

  const handleNewPaymentSubmit = () => {
    const newPayment: PaymentUIModel = {
      reservationId: String(data.reservation?.id) || '',
      unitTypeId: data.reservation?.unitTypeId || '',
      roomId: data.reservation?.roomId || '',
      dueDate: newPaymentDueDate.value?.getTime(),
      checkInDate: Number(data.reservation?.checkInDate),
      checkOutDate: Number(data.reservation?.checkOutDate),
      areaId: data.reservation?.area,
      userId: data.reservation?.userId,
      document: newPaymentItems,
      direction: newPaymentDirection.value,
      paymentItems: newPaymentItems,
      status: PaymentStatus.PENDING,
      collector: PaymentCollector.BIRDNEST,
      paymentType: PaymentType.CASH,
      unitTypeGroupId: String(data.reservation?.unitTypeGroupId),
      landlordId: String(data.reservation?.landlordId),
      propertyId: String(data.reservation?.propertyId),
      source: String(data.reservation?.source),
      bankCharges: 0,
      bankChargesCurrency: newPaymentCurrency.value,
      forexDifference: 0
    };
    eventHandlers.addPaymentHandler(newPayment);
  };

  const isSubmitDisabled =
    !newPaymentItems.every(
      (element) => element.feeName && element.feeType && element.feeValue && element.itemType
    ) || !newPaymentDueDate.isValid;

  const paymentTypeOptions: { label: string; value: string }[] = hasAccess([
    PERMISSIONS_LIST.paymentsFullAccess
  ])
    ? [
        { label: 'Select One', value: '' },
        { label: 'Rent', value: PaymentItemType.rent },
        { label: 'Cleaning', value: PaymentItemType.cleaning },
        { label: 'Utilities', value: PaymentItemType.utilities },
        { label: 'Insurance', value: PaymentItemType.insurance },
        { label: 'Others', value: PaymentItemType.others }
      ]
    : [
        { label: 'Select One', value: '' },
        { label: 'Cleaning', value: PaymentItemType.cleaning },
        { label: 'Utilities', value: PaymentItemType.utilities },
        { label: 'Insurance', value: PaymentItemType.insurance },
        { label: 'Others', value: PaymentItemType.others }
      ];

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, maxWidth: 'lg', showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.closeHandler();
        }
      }}
    >
      {/* Body */}
      <div className="px-4 sm:px-10 pt-2 pb-4 flex flex-col gap-5 min-h-[40vh]">
        {/* Title */}
        <div>
          <h2 className="text-2xl font-bold text-[#37454d] font-[Nunito-Bold,Nunito,sans-serif]">
            Add payment
          </h2>
          <hr className="mt-3 border-gray-200" />
        </div>

        {/* Direction + Currency row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-[#37454d]">Direction</label>
            <Select
              options={[
                { label: 'PAY-IN', value: 'in' },
                { label: 'PAY-OUT', value: 'out' }
              ]}
              defaultValue={{ label: 'PAY-IN', value: 'in' }}
              onChange={(value: ValueType<{ value: string; label: string }, false>) => {
                setNewPaymentDirection({
                  value: value ? (value.value as 'in' | 'out') : 'in',
                  isValid: true
                });
              }}
              classNamePrefix="react-select"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-[#37454d]">Currency</label>
            <Select
              options={[
                { label: 'EGP', value: 'egp' },
                { label: '$', value: 'usd' }
              ]}
              defaultValue={{ label: 'EGP', value: 'egp' }}
              onChange={(value: ValueType<{ value: string; label: string }, false>) => {
                setNewPaymentCurrency({
                  value: value ? (value.value as 'egp' | 'usd') : 'egp',
                  isValid: true
                });
              }}
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {/* Due Date row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold text-[#37454d]">Due Date</label>
            <ReactDatePicker
              selected={newPaymentDueDate.value}
              onChange={(date) => setNewPaymentDueDate({ value: date as Date, isValid: true })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Payment Items */}
        <div className="bg-gray-100 rounded-xl p-4 flex flex-col gap-3">
          {/* Items header */}
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-[#37454d]">Payment Items</span>
            <button
              type="button"
              className="text-4xl flex items-center text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              onClick={() => {
                setNewPaymentItems([
                  ...newPaymentItems,
                  { feeName: '', feeType: FeeType.AMOUNT, itemType: undefined, feeValue: 0 }
                ]);
              }}
            >
              <AddBoxIcon color="primary" fontSize="inherit" />
            </button>
          </div>

          {/* Column headers — hidden on mobile, shown on sm+ */}
          <div className="hidden sm:grid sm:grid-cols-[1fr_1fr_1fr_40px] gap-3">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Fee Name
            </span>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Payment Type
            </span>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Amount
            </span>
            <span />
          </div>

          {/* Item rows */}
          {newPaymentItems.map((paymentItem, index) => (
            <div
              key={index}
              className="flex flex-col sm:grid sm:grid-cols-[1fr_1fr_1fr_40px] gap-3 bg-white sm:bg-transparent rounded-lg sm:rounded-none p-3 sm:p-0 border border-gray-200 sm:border-none"
            >
              {/* Fee Name */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-400 sm:hidden">Fee Name</label>
                <InputText
                  data={{
                    value: paymentItem.feeName,
                    errorMessage: 'Fee Name is required',
                    placeholder: 'Fee name'
                  }}
                  config={{
                    maxRows: 1,
                    autoFocus: index === 0,
                    required: true,
                    showErrorMode: InputTextErrorMode.instant
                  }}
                  eventHandlers={{
                    onChangeHandler: (value: string) => {
                      const updated = [...newPaymentItems];
                      updated[index] = { ...updated[index], feeName: value };
                      setNewPaymentItems(updated);
                    }
                  }}
                />
              </div>

              {/* Payment Type */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-400 sm:hidden">
                  Payment Type
                </label>
                <Select
                  options={paymentTypeOptions}
                  value={
                    newPaymentItems[index].itemType
                      ? {
                          label: String(newPaymentItems[index].itemType),
                          value: String(newPaymentItems[index].itemType)
                        }
                      : { label: 'Select One', value: '' }
                  }
                  styles={{
                    control: (base) => ({ ...base, minHeight: '48px', height: '48px' } as any),
                    valueContainer: (base) =>
                      ({ ...base, height: '48px', padding: '0 12px' } as any),
                    indicatorsContainer: (base) => ({ ...base, height: '48px' } as any),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 } as any)
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  onChange={(value: ValueType<{ value: string; label: string }, false>) => {
                    const updated = [...newPaymentItems];
                    updated[index] = {
                      ...updated[index],
                      itemType: (value?.value as PaymentItemType) || undefined
                    };
                    setNewPaymentItems(updated);
                  }}
                  classNamePrefix="react-select"
                />
              </div>

              {/* Amount */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-400 sm:hidden">Amount</label>
                <InputText
                  data={{
                    value: newPaymentItems[index].feeValue,
                    errorMessage: 'Amount is required',
                    placeholder: 'Amount'
                  }}
                  config={{
                    maxRows: 1,
                    required: true,
                    type: InputTextType.number,
                    showErrorMode: InputTextErrorMode.instant
                  }}
                  eventHandlers={{
                    onChangeHandler: (value: string) => {
                      const updated = [...newPaymentItems];
                      updated[index] = { ...updated[index], feeValue: Number(value) };
                      setNewPaymentItems(updated);
                    }
                  }}
                />
              </div>

              {/* Delete */}
              <div className="flex items-center justify-end sm:justify-center">
                <button
                  type="button"
                  className={`transition-opacity ${
                    index === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100 cursor-pointer'
                  }`}
                  onClick={() => {
                    setNewPaymentItems(newPaymentItems.filter((_, i) => i !== index));
                  }}
                >
                  <DeleteIcon fontSize="large" color="error" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 px-4 sm:px-10 pb-6 pt-2">
        <Button
          onClick={() => eventHandlers.closeHandler()}
          type={ButtonType.primaryInverted}
          tracking={{ event: 'record_edit_cancelled', entity: 'payment', method: 'close' }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Cancel
        </Button>
        <Button
          disabled={isSubmitDisabled}
          onClick={handleNewPaymentSubmit}
          tracking={{
            event: 'record_saved',
            entity: 'payment',
            action: 'update',
            outcome: 'success'
          }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Add payment
        </Button>
      </div>
    </Modal>
  );
};
